import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
// Порт ref DesignBlockEditor.tsx:237-316: в правке — авто-растущая textarea без рамок,
// в read-only — h2. Теги из текста режем (плейн-текст), но БЕЗ trim: иначе набранный
// пробел на конце тут же срезается.
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { cn } from '@teach/ui'
import { useStrictLang } from './strictLangContext'
import type { DesignHeading } from '@/types/blocks'
import { stripToPlainText } from '@/features/editor/lib/designHtml'
import { DESIGN_LIMITS } from '@/features/editor/lib/designElements'
import { getLocalizedDisplay, setLocalizedField } from '@/features/editor/lib/localizedField'
import { autoGrowTextArea } from './autoGrowTextArea'
import {
  alignClass,
  backdropTextColor,
  fontWeightClass,
  headingSizeClass,
  TEXT_LINE_PADDING_X,
} from '@/features/editor/lib/designElementStyles'

// Край заголовка — на линии текста в подложке, а не на краю подложки.
const LINE_STYLE = { paddingInline: TEXT_LINE_PADDING_X }
import type { DesignElementProps } from './DesignElementView'

export const HeadingElement = ({
  element,
  editable,
  contentLang,
  onUpdate,
  onSelect,
  isRun,
}: DesignElementProps<DesignHeading>) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const strictLang = useStrictLang()
  // Симметричный фолбэк только для показа: пустой перевод неотличим от «не заполнено».
  const shown = stripToPlainText(
    getLocalizedDisplay(element, 'content', contentLang, editable || strictLang),
  )
  const classes = cn(
    headingSizeClass(element.textSize),
    fontWeightClass(element.fontWeight),
    alignClass(element.textAlign),
  )
  // Заголовок своей подложки не имеет — тот же резолвер, что у TextElement
  // (backdrop='none'), а не отдельное чтение blockTheme.designBlock.text
  // напрямую: значение сегодня совпадает, но способов получить его должен
  // быть один — иначе будущая тёмная тема (светлый текст) приземлится
  // в heading/table/text по отдельности вместо одного места.
  const color = backdropTextColor('none')

  if (!editable) {
    return (
      <div className="w-full" style={LINE_STYLE}>
        {/* font-madefor задан и на корне канваса (наследованием кроет текст/бейджи/таблицу),
            и здесь ещё раз — global src/index.css бьёт `h1..h6 { font-family: Inter }` прямо
            по тегу. Прямое правило на элементе побеждает любое наследование независимо от
            специфичности, поэтому унаследованный шрифт тут не срабатывает. Наш класс, стоя
            на самом h2, конкурирует уже как class-селектор (0,1,0) против их тег-селектора
            (0,0,1) — и выигрывает. Не убирать ни один из двух: это не дублирование. */}
        <h2
          className={cn(
            classes,
            // tracking-normal — компенсация global src/index.css: там правило
            // `h1..h6 { @apply font-semibold tracking-tight }` бьёт letter-spacing
            // прямо по тегу (-.025em), а макет трекинга не задаёт вовсе. Шрифт и
            // жирность здесь уже перебиты (font-madefor/fontWeightClass) — трекинг
            // без этого класса остался бы единственным неперебитым правилом из
            // глобального набора. Не убирать как «лишний»: без него заголовок
            // просто визуально не ужат в jsdom-тестах, разница видна только
            // в реальном рендере.
            'font-madefor whitespace-pre-wrap break-words leading-[normal] tracking-normal',
          )}
          style={{ color }}
        >
          {/* Прохождение: плеер академии рисует пустой заголовок без фолбэка —
              «Заголовок» здесь плейсхолдер формы, авторский, ученик его не увидит. */}
          {isRun ? shown : shown || m.heading}
        </h2>
      </div>
    )
  }

  return (
    <div className="w-full" style={LINE_STYLE}>
      <textarea
        value={shown}
        rows={1}
        maxLength={DESIGN_LIMITS.HEADING}
        placeholder={cm.heading}
        onChange={(e) =>
          onUpdate(
            setLocalizedField(
              'content',
              contentLang,
              e.target.value.slice(0, DESIGN_LIMITS.HEADING),
            ),
          )
        }
        onFocus={onSelect}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        ref={(el) => autoGrowTextArea(el)}
        // Тот же приём, что в read-only ветке выше: `textarea` не попадает под
        // `h1..h6`, но правило дублируем всё равно, чтобы обе ветки заголовка
        // держали шрифт одинаково явно, а не по случайности разных тегов. Это
        // касается и tracking-normal — `textarea` тем более не подпадает под
        // глобальное `h1..h6`-правило, но без явного класса текст «дышал» бы
        // при входе в редактирование (h2 в read-only ужат global tracking-tight,
        // а textarea в правке — нет).
        className={cn(
          classes,
          'font-madefor m-0 block w-full resize-none overflow-hidden break-words border-none bg-transparent p-0 leading-[normal] tracking-normal outline-none',
        )}
        style={{ color }}
      />
    </div>
  )
}
